<template>
  <AppModal
    v-if="authenticationStep === 1"
    class="authentication-modal"
    size="xl"
    :disabled="isSubmitting"
    @close="$emit('close')"
  >
    <form
      autocomplete="off"
      @submit.prevent="submit"
    >
      <div class="col-span-12 mx-auto text-center">
        <h2>{{ title }}</h2>

        <p>
          {{ text }}
        </p>

        <p v-if="intent === IntentType.Checkout">
          You don’t need a SportPix account to continue,
          but if you have one already, you’ll automatically be logged in upon confirming.
        </p>

        <TextInput
          v-model="formData.email"
          label="Enter your Email"
          required
          class="mb-8"
          :validation-error="errors"
          autofocus
        />

        <AppButton
          :processing="isSubmitting"
          :disabled="cooldown !== 0"
          class="btn btn-solid py-2.5 w-full mb-6"
          :class="{'btn-gray': (cooldown > 0), 'btn-secondary': (cooldown < 1) }"
        >
          {{ isSubmitting ? 'Sending a ' + link : 'Send a ' + link }}
        </AppButton>

        <template v-if="cooldown">
          <p class="text-gray-500 p-2 bg-gray-100 rounded w-full mb-0">
            {{ cooldown === 0 ? 'Request another code' : `Request another code (${cooldown})` }}
          </p>
        </template>

        <template v-if="error">
          <p class="error-message">
            {{ error }}
          </p>
        </template>
      </div>
    </form>
  </AppModal>

  <AppModal
    v-else-if="authenticationStep === 2"
    size="xl"
    class="authentication-modal"
    :disabled="isSubmitting"
    @close="$emit('close')"
  >
    <AuthenticationCodeForm
      v-model:is-submitting="isSubmitting"
      :email="formData.email"
      @close="$emit('close')"
      @submit="$emit('submit')"
    />
  </AppModal>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { reactive, ref } from 'vue';

import { AppButton, AppModal } from '@/components/app';
import { AuthenticationCodeForm } from '@/components/forms';
import { TextInput } from '@/components/forms/inputs';

import { useFormSubmit, useHttp } from '@/composables';
import { IntentType } from '@/const';

const { isSubmitting, errors, error } = useFormSubmit();

defineEmits(['close', 'submit']);

const props = defineProps({
  intent: {
    type: String,
    default: null,
  },
});

const authenticationStep = ref(1);

const formData = reactive({
  email: import.meta.env.DEV ? 'admin@example.com' : null,
});

const title = ref(null);
const text = ref(null);
const link = ref(null);
const endpoint = ref(null);
const cooldown = ref(0);

switch (props.intent) {
  case IntentType.Login:
    title.value = 'Login';
    text.value = 'We know passwords can be a hassle. Simply enter your email address, and we’ll quickly send you a secure, single-use code that you can use to log in.';
    link.value = 'login code';
    endpoint.value = '/auth/login';
    break;
  case IntentType.Register:
    title.value = 'Register';
    text.value = 'We know passwords can be a hassle. Simply enter your email address, and we’ll quickly send you a secure, single-use code that you can use to register with.';
    link.value = 'registration code';
    endpoint.value = '/auth/register';
    break;
  case IntentType.Checkout:
    title.value = 'Email Confirmation';
    text.value = 'We’ll send your digital photos to this email address, so let’s quickly confirm it’s right. Enter your email, and we\'ll send you a one-time confirmation code.';
    link.value = 'confirmation code';
    endpoint.value = '/auth/login';
    break;
}

const submit = async () => {
  errors.value = null;
  error.value = null;
  isSubmitting.value = true;

  let response;

  response = await useHttp().post(endpoint.value, formData);

  // If the intent is Checkout and the user is unauthorized (the user doesn't exist) then  register them as a new user
  if (props.intent === IntentType.Checkout && response.statusCode === HttpStatusCode.Unauthorized) {
    response = await useHttp().post('/auth/register', formData);
  }

  if (response.statusCode === HttpStatusCode.Ok) {
    authenticationStep.value = 2;
  } else if (response.statusCode === HttpStatusCode.UnprocessableEntity) {
    errors.value = response.errors.email[0];
  } else if (response.statusCode === HttpStatusCode.TooManyRequests) {
    error.value = response.exception;
    isSubmitting.value = false;

    cooldown.value = error.value.replace(/.*?(\d+).*/, '$1');
    error.value = error.value.split('.')[0];

    while (cooldown.value > 0) {
      await new Promise(resolve => {
        setTimeout(() => {
          cooldown.value -= 1;

          resolve();
        }, 1000);
      });
    }
  } else {
    errors.value = response.exception;
  }

  isSubmitting.value = false;
};

</script>
